<template>
  <div class="tile">
    <div class="tile-label">{{ label }}</div>
    <div class="tile-row">
      <span class="tile-value">{{ value }}</span>
      <svg v-if="spark && spark.length > 1" class="spark" :viewBox="`0 0 ${W} ${H}`" :aria-label="`${label} trend`">
        <line
          v-if="benchmarkY !== null"
          :x1="0" :x2="W" :y1="benchmarkY" :y2="benchmarkY" class="spark-benchmark"
        />
        <path :d="sparkPath" fill="none" class="spark-line" />
        <circle :cx="lastPoint[0]" :cy="lastPoint[1]" r="3" class="spark-dot" />
      </svg>
    </div>
    <div v-if="delta" class="tile-delta">
      <span v-if="statusKey" class="chip" :class="`chip-${statusKey}`" aria-hidden="true"></span>
      <span>{{ delta }}</span>
    </div>
  </div>
</template>

<script>
import { scaleLinear, linePath } from '../../utils/chartUtils';

export default {
  name: 'StatTile',
  props: {
    label: { type: String, required: true },
    value: { type: String, required: true },
    delta: { type: String, default: '' },
    statusKey: { type: String, default: '' }, // good | warning | serious | critical
    spark: { type: Array, default: null },
    benchmark: { type: Number, default: null },
  },
  data: () => ({ W: 110, H: 34 }),
  computed: {
    sparkScales() {
      const vals = this.benchmark === null ? this.spark : [...this.spark, this.benchmark];
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const pad = (max - min || 1) * 0.18;
      return {
        x: scaleLinear([0, this.spark.length - 1], [2, this.W - 6]),
        y: scaleLinear([min - pad, max + pad], [this.H - 4, 4]),
      };
    },
    sparkPath() {
      const { x, y } = this.sparkScales;
      return linePath(this.spark.map((v, i) => [x(i), y(v)]));
    },
    lastPoint() {
      const { x, y } = this.sparkScales;
      const i = this.spark.length - 1;
      return [x(i), y(this.spark[i])];
    },
    benchmarkY() {
      if (this.benchmark === null || !this.spark) return null;
      return this.sparkScales.y(this.benchmark);
    },
  },
};
</script>

<style scoped>
.tile { padding: 14px 18px 12px; min-width: 0; }
.tile-label {
  font-size: 10px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--muted); margin-bottom: 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.tile-row { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; }
.tile-value {
  font-size: 30px; font-weight: 800; color: var(--ink); line-height: 1;
  letter-spacing: -0.02em; white-space: nowrap;
}
.tile-delta {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: var(--ink); margin-top: 8px; font-variant-numeric: tabular-nums;
}
.chip { width: 8px; height: 8px; flex-shrink: 0; }
.chip-good { background: var(--status-good); }
.chip-warning { background: var(--status-warning); }
.chip-serious { background: var(--status-serious); }
.chip-critical { background: var(--red); }
.spark { width: 110px; height: 34px; flex-shrink: 0; }
.spark-line { stroke: var(--ink); stroke-width: 1.3; stroke-dasharray: 2 2.5; }
.spark-dot { fill: var(--red); }
.spark-benchmark { stroke: var(--hairline); stroke-width: 1; }
</style>
